import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CheckCircle, AlertCircle, Award, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// AHEPA organization affiliations
const AFFILIATIONS = [
  { value: 'AHEPA', label: 'AHEPA (Order of AHEPA)' },
  { value: 'DOP', label: 'Daughters of Penelope' },
];

const EndorsementPage = () => {
  const { token } = useParams();
  const [pageState, setPageState] = useState('loading'); // 'loading' | 'form' | 'success' | 'error'
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [endorsementInfo, setEndorsementInfo] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    endorser_name: '',
    affiliation: '',
    chapter: '',
    endorsement_confirmed: false
  });

  useEffect(() => {
    const fetchEndorsementInfo = async () => {
      try {
        const response = await axios.get(`${API}/endorsement/${token}`);
        setEndorsementInfo(response.data);
        setFormData(prev => ({
          ...prev,
          endorser_name: response.data.endorser_name || ''
        }));
        setPageState('form');
      } catch (err) {
        setErrorMessage(err.response?.data?.detail || 'Invalid or expired endorsement link');
        setPageState('error');
      }
    };
    
    if (pageState === 'loading') {
      fetchEndorsementInfo();
    }
  }, [token, pageState]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!formData.endorsement_confirmed) {
      toast.error('Please confirm the endorsement by checking the checkbox');
      return;
    }
    
    if (!formData.endorser_name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    if (!formData.affiliation) {
      toast.error('Please select your affiliation');
      return;
    }
    
    if (!formData.chapter.trim()) {
      toast.error('Please enter your chapter name/number');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/endorsement/${token}`, formData);
      console.log('Endorsement response:', response.data);
      toast.success('Endorsement submitted successfully!');
      // Use state machine to transition to success
      setPageState('success');
    } catch (err) {
      console.error('Endorsement error:', err.response?.data || err);
      toast.error(err.response?.data?.detail || 'Failed to submit endorsement');
      setSubmitting(false);
    }
  };

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-greek-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (pageState === 'error') {
    const handleClose = () => {
      window.close();
      setTimeout(() => {
        toast.info('You can safely close this tab now.');
      }, 100);
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white rounded-sm border border-slate-200 p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">Invalid Link</h1>
          <p className="text-slate-600 mb-6">{errorMessage}</p>
          <Button 
            variant="outline" 
            className="rounded-sm"
            onClick={handleClose}
            data-testid="close-error-btn"
          >
            Close
          </Button>
          <p className="text-xs text-slate-400 mt-4">You may safely close this window.</p>
        </div>
      </div>
    );
  }

  if (pageState === 'success') {
    const handleClose = () => {
      // Try to close the window/tab
      window.close();
      // If window.close() doesn't work (e.g., if the page wasn't opened via script),
      // show a message to the user
      setTimeout(() => {
        // If we're still here after 100ms, the window didn't close
        toast.info('You can safely close this tab now.');
      }, 100);
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white rounded-sm border border-slate-200 p-8 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">Endorsement Complete!</h1>
          <p className="text-slate-600 mb-6">
            Thank you for endorsing {endorsementInfo?.applicant_name}'s scholarship application. 
            The applicant has been notified and can now submit their application.
          </p>
          <Button 
            onClick={handleClose}
            className="bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm"
            data-testid="close-endorsement-btn"
          >
            Close
          </Button>
          <p className="text-xs text-slate-400 mt-4">You may safely close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-6 py-6">
          <div className="flex items-center justify-center gap-3">
            <div className="w-12 h-12 bg-greek-blue rounded-full flex items-center justify-center">
              <Award className="w-6 h-6 text-white" />
            </div>
            <div className="text-center">
              <h1 className="font-heading text-xl font-bold text-slate-900">AHEPA Scholarship</h1>
              <p className="text-sm text-slate-500">Chapter Endorsement Request</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8">
        <div className="bg-white rounded-sm border border-slate-200 p-8">
          {/* Applicant Info */}
          <div className="mb-8 p-4 bg-slate-50 rounded-sm">
            <h2 className="font-semibold text-slate-800 mb-2">Endorsement Request</h2>
            <p className="text-slate-600">
              You have been requested to endorse the following scholarship application:
            </p>
            <div className="mt-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500">Applicant:</span>
                <span className="font-medium text-slate-800">{endorsementInfo?.applicant_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Scholarship:</span>
                <span className="font-medium text-slate-800">{endorsementInfo?.scholarship_name}</span>
              </div>
            </div>
          </div>

          {/* Endorsement Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="endorser_name">Your Full Name *</Label>
              <Input
                id="endorser_name"
                value={formData.endorser_name}
                onChange={(e) => setFormData({ ...formData, endorser_name: e.target.value })}
                placeholder="Enter your full name"
                className="rounded-sm"
                data-testid="input-endorser-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="affiliation">Your AHEPA Affiliation *</Label>
              <Select
                value={formData.affiliation}
                onValueChange={(value) => setFormData({ ...formData, affiliation: value })}
              >
                <SelectTrigger className="rounded-sm" data-testid="select-affiliation">
                  <SelectValue placeholder="Select your affiliation" />
                </SelectTrigger>
                <SelectContent>
                  {AFFILIATIONS.map((aff) => (
                    <SelectItem key={aff.value} value={aff.value}>{aff.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="chapter">Chapter Name/Number *</Label>
              <Input
                id="chapter"
                value={formData.chapter}
                onChange={(e) => setFormData({ ...formData, chapter: e.target.value })}
                placeholder="e.g., Chapter #123 or Athena Chapter"
                className="rounded-sm"
                data-testid="input-chapter"
              />
            </div>

            {/* Endorsement Confirmation */}
            <div className="p-4 bg-greek-blue/5 border border-greek-blue/20 rounded-sm">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="endorsement_confirmed"
                  checked={formData.endorsement_confirmed}
                  onCheckedChange={(checked) => setFormData({ ...formData, endorsement_confirmed: checked })}
                  className="mt-1"
                  data-testid="checkbox-confirm-endorsement"
                />
                <label htmlFor="endorsement_confirmed" className="text-sm text-slate-700 cursor-pointer">
                  <span className="font-medium">I hereby endorse this applicant</span>
                  <p className="mt-1 text-slate-500">
                    By checking this box, I confirm that I am a member in good standing of the 
                    AHEPA family of organizations and that I endorse {endorsementInfo?.applicant_name}'s 
                    application for the {endorsementInfo?.scholarship_name}.
                  </p>
                </label>
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting || !formData.endorsement_confirmed}
              className="w-full bg-greek-gold hover:bg-greek-gold-dark text-white rounded-sm py-6 text-lg"
              data-testid="submit-endorsement-btn"
              onClick={(e) => {
                // Prevent any default behavior
                if (submitting) {
                  e.preventDefault();
                  return;
                }
              }}
            >
              {submitting ? (
                <>Submitting...</>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Submit Endorsement
                </>
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          This endorsement link is valid for 30 days. If you have any questions, 
          please contact the scholarship administrator.
        </p>
      </main>
    </div>
  );
};

export default EndorsementPage;
